import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { androidStores, app, assetUrl, storeWebUrl } from '../config.js';
import BrowserGuide from '../components/BrowserGuide.jsx';
import ShareCard from '../components/ShareCard.jsx';
import Toast, { useToast } from '../components/Toast.jsx';
import { copyText } from '../lib/clipboard.js';
import { buildAppLink, tryOpenApp } from '../lib/deeplink.js';
import { detectBrowser, isMobile } from '../lib/platform.js';
import { decodeShareToken, isAllowedInAppPath, normalizeInAppPath } from '../lib/share-token.js';

/**
 * 分享落地页 —— 整个站点的核心。
 *
 * 三种人打开同一个链接，要走三条不同的路：
 *
 *   1. 装了 App 并且在系统浏览器里
 *      → 系统层面就会被 App Links / Universal Links 拦走，本页根本不加载。
 *        所以「本页被加载了」本身就已经说明：要么没装，要么在拦 scheme 的
 *        内置浏览器里，要么在电脑上。
 *
 *   2. 没装 App
 *      → 尝试唤起后超时，判定未安装，重定向到首页做下载引导。
 *
 *   3. 在微信 / QQ 里
 *      → scheme 一定被拦，硬跳只会弹错误。改成展示「用浏览器打开」的引导。
 *
 * 链接形态有两种，本页都处理：
 *
 *   1. App 路由直连（主形态）：/posts/1
 *      分享链接就是 https://host/posts/1，App 收到同样的 path 直接能路由，
 *      App 侧零改动。没装 App 的用户直接落到本站（站点就挂在这个域名的根）。
 *
 *   2. token 形态（可选）：/s/<token>
 *      能在链接里带卡片标题/描述，但要 App 侧解一次 token。
 */
export default function SharePage() {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState('opening');
  const [guideOpen, setGuideOpen] = useState(false);
  const [toast, showToast] = useToast();

  const browser = useMemo(() => detectBrowser(), []);

  /**
   * 解析分享内容。
   * - /s/<token>：token 里带 path + 卡片文案
   * - 其它任何路径：把 URL 路径原样当作 App 内路由（/posts/1）
   *   location.pathname 已经去掉 basename（/web），所以拿到的就是 /posts/1。
   *   兼容历史上的 /go 前缀（/go/posts/1 等价于 /posts/1），留着不影响。
   */
  const share = useMemo(() => {
    if (token) {
      const decoded = decodeShareToken(token);
      // token 里不带 query，补个空串让下游不用判断 undefined
      return decoded ? { ...decoded, search: '' } : null;
    }

    const rawPath = location.pathname.replace(/^\/go(?=\/|$)/, '');
    const path = normalizeInAppPath(rawPath);

    return {
      path,
      // 商品 / 优惠券的 ?shopId= 是 App 路由的必填入参，必须原样带给 App，
      // 否则 App 会渲染「缺少商品或店铺参数」。
      search: location.search || '',
      title: '',
      description: '',
      image: '',
      author: '',
      // 白名单只看 pathname，query 不参与
      valid: isAllowedInAppPath(path),
    };
  }, [token, location.pathname, location.search]);

  /** 在微信里「直接去下载」走应用宝网页版——微信放行这个域名。 */
  const wechatDownloadUrl = useMemo(() => {
    const tencent = androidStores.find((store) => store.id === 'tencent');
    return tencent ? storeWebUrl(tencent) : '';
  }, []);

  useEffect(() => {
    // 链接解不出来 / 落地路径不在白名单：当作失效链接送回首页
    if (!share || !share.valid) {
      navigate('/', { replace: true, state: { reason: 'invalid' } });
      return;
    }

    // 电脑上没有「唤起 App」这回事，直接带去首页做扫码/复制引导
    if (!isMobile()) {
      navigate('/', { replace: true, state: { reason: 'desktop', share } });
      return;
    }

    // 微信 / QQ 这类内置浏览器一定拦 scheme，不硬跳
    if (browser.blocksScheme) {
      setStatus('blocked');
      setGuideOpen(true);
      return;
    }

    setStatus('opening');

    // query 也要带进 key：同一路径带不同 ?shopId= 是不同的深链
    tryOpenApp({
      url: buildAppLink(share.path, share.search),
      key: `share:${share.path}${share.search}`,
    }).then((outcome) => {
      if (outcome === 'opened') {
        setStatus('opened');
        return;
      }

      // 超时后仍停在浏览器 = 没装 App。
      // 按需求在这里「拦截」分享链接，重定向到首页给出下载引导。
      navigate('/', { replace: true, state: { reason: 'not-installed', share } });
    });
  }, [share, navigate, browser]);

  const goHome = (reason) => {
    navigate('/', { state: { reason, share } });
  };

  /** 「没有反应就再试一次」：换一个 key，让 tryOpenApp 重新真的尝试一遍。 */
  const retryOpen = () => {
    if (!share?.valid) return;

    setStatus('opening');
    tryOpenApp({
      url: buildAppLink(share.path, share.search),
      key: `share-retry:${share.path}${share.search}:${Date.now()}`,
    }).then((outcome) => {
      if (outcome === 'opened') {
        setStatus('opened');
        return;
      }
      goHome('not-installed');
    });
  };

  const handleCopy = async () => {
    const ok = await copyText(window.location.href);
    showToast(ok ? '链接已复制，去手机浏览器粘贴打开' : '复制失败，请长按地址栏复制');
  };

  return (
    <div className="page">
      <div className="content">
        <header className="brand">
          <img className="brand__icon" src={assetUrl('img/app-icon.png')} alt={app.name} />
          <h1 className="brand__name">{app.name}</h1>
        </header>

        <ShareCard share={share} />

        <div className="share-status">
          {status === 'opening' ? (
            <>
              <div className="share-status__spinner" aria-hidden="true" />
              <p className="share-status__text">正在尝试打开「{app.name}」…</p>
            </>
          ) : null}

          {status === 'opened' ? (
            <>
              <p className="share-status__text">
                已尝试唤起 App。如果页面没有跳转，说明这台手机还没安装。
              </p>
              <div className="share-status__actions">
                <button type="button" className="btn btn--primary" onClick={retryOpen}>
                  再打开一次
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => goHome('manual')}>
                  先下载 App
                </button>
              </div>
            </>
          ) : null}

          {status === 'blocked' ? (
            <>
              <p className="share-status__text">
                当前是在「{browser.name}」里打开这个链接的，需要换到手机浏览器才能唤起 App。
              </p>
              <div className="share-status__actions">
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => setGuideOpen(true)}
                >
                  查看打开方法
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => goHome('manual')}>
                  先下载 App
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {guideOpen ? (
        <BrowserGuide
          browserName={browser.name}
          downloadUrl={wechatDownloadUrl}
          onCopy={handleCopy}
          onClose={() => setGuideOpen(false)}
        />
      ) : null}

      <Toast message={toast} />
    </div>
  );
}
